'use client';
import React, { useState, useEffect, Suspense } from 'react';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import AppLogo from '@/components/ui/AppLogo';
import { FileText, Shield, DollarSign, Handshake, ChevronRight, ArrowLeft } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const SECTIONS = [
  { id: 'terms', label: 'Terms of Service', icon: FileText, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
  { id: 'privacy', label: 'Privacy Policy', icon: Shield, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/20' },
  { id: 'fees', label: 'Fee Schedule', icon: DollarSign, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
  { id: 'affiliate', label: 'Affiliate Agreement', icon: Handshake, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/20' },
];

const DEFAULT_CONTENT: Record<string, string> = {
  terms: `**Terms of Service**\n\nLast updated: April 2026\n\n**1. Acceptance of Terms**\nBy accessing or using HireTruckers365 ("Platform"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Platform.\n\n**2. Platform Description**\nHireTruckers365 is a private trucking recruitment marketplace. All driver-employer introductions are managed exclusively by platform administrators to ensure quality and compliance.\n\n**3. User Accounts**\nYou are responsible for maintaining the confidentiality of your account credentials. You agree to provide accurate, current, and complete information during registration.\n\n**4. Referral Fee**\nEmployers agree to pay a referral fee equal to 20% of the placed driver's first-year gross salary upon a successful hire facilitated through the Platform. This fee is non-negotiable and due within 30 days of the driver's start date.\n\n**5. Prohibited Conduct**\nUsers may not circumvent the Platform to hire drivers introduced through the Platform without paying the applicable referral fee. Violation may result in legal action and account termination.\n\n**6. Limitation of Liability**\nHireTruckers365 is not liable for any indirect, incidental, or consequential damages arising from use of the Platform.\n\n**7. Governing Law**\nThese Terms are governed by the laws of the State of Texas, without regard to conflict of law principles.\n\n**8. Changes to Terms**\nWe reserve the right to modify these Terms at any time. Continued use of the Platform constitutes acceptance of updated Terms.`,
  privacy: `**Privacy Policy**\n\nLast updated: April 2026\n\n**1. Information We Collect**\nWe collect information you provide directly (name, email, CDL number, employment history) and information collected automatically (IP address, browser type, usage data).\n\n**2. How We Use Your Information**\nWe use collected information to: match drivers with employers, process referral fees, communicate platform updates, and improve our services.\n\n**3. Information Sharing**\nWe do not sell your personal information. Driver profiles are shared only with employers selected by our admin team. Employer contact details are shared only with matched drivers.\n\n**4. Data Security**\nWe implement industry-standard security measures including encryption at rest and in transit, access controls, and regular security audits.\n\n**5. Cookies**\nWe use cookies to maintain session state and analyze platform usage. You may disable cookies in your browser settings, though some features may not function properly.\n\n**6. Data Retention**\nWe retain your data for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data at any time.\n\n**7. Your Rights**\nYou have the right to access, correct, or delete your personal information. Contact us at privacy@hiretruckers365.com to exercise these rights.\n\n**8. Contact**\nFor privacy-related inquiries, contact: privacy@hiretruckers365.com`,
  fees: `**Fee Schedule**\n\nLast updated: April 2026\n\n**Driver Fees**\n- Registration: Free\n- Profile creation and maintenance: Free\n- Job matching and introductions: Free\n- Affiliate referral earnings: 10% of the referral fee collected on successful placement of referred driver\n\n**Employer Fees**\n- Job posting: Free\n- Candidate browsing: Not available (admin-curated introductions only)\n- Referral fee upon successful hire: 20% of placed driver's first-year gross salary\n- Referral fee payment terms: Net 30 from driver start date\n- Late payment fee: 1.5% per month on outstanding balances\n\n**Affiliate Program**\n- Driver referral bonus: 10% of the referral fee collected when a referred driver is successfully placed\n- Employer referral credit: Applied as credit toward next referral fee invoice\n- Minimum payout threshold: $50\n- Payout schedule: Monthly, via ACH or check\n\n**Invoice & Payment**\n- All referral fee invoices are issued electronically via the Platform\n- Accepted payment methods: ACH bank transfer, wire transfer, major credit cards\n- Disputed invoices must be raised within 10 business days of receipt\n\n**Fee Changes**\nHireTruckers365 reserves the right to modify this fee schedule with 30 days' written notice to active users.`,
  affiliate: `**Affiliate Agreement**\n\nLast updated: April 2026\n\n**1. Program Overview**\nThe HireTruckers365 Affiliate Program allows registered users to earn compensation by referring new drivers or employers to the Platform.\n\n**2. Eligibility**\nAny registered user in good standing may participate in the Affiliate Program. Participation is subject to approval and ongoing compliance with this Agreement.\n\n**3. Driver Referral Track**\nWhen a driver you refer is successfully placed with an employer through the Platform:\n- You earn 10% of the referral fee collected from the employer\n- The referral must be made through your unique referral link\n- The referred driver must complete onboarding and be placed within 12 months of registration\n\n**4. Employer Referral Track**\nWhen an employer you refer makes a successful hire through the Platform:\n- You earn a credit equal to a portion of the referral fee, applied to your next invoice\n- The referral must be made through your unique referral link\n- Credits are non-transferable and non-redeemable for cash\n\n**5. Referral Attribution**\nReferrals are tracked via unique referral links. Last-click attribution applies. Self-referrals are prohibited and will result in disqualification.\n\n**6. Payment**\nAffiliate earnings are paid monthly, provided the minimum threshold of $50 is met. Payments are made via ACH or check within 15 business days of month end.\n\n**7. Prohibited Activities**\nAffiliates may not use spam, misleading advertising, or any deceptive practices to generate referrals. Violation results in immediate termination and forfeiture of unpaid earnings.\n\n**8. Termination**\nEither party may terminate participation in the Affiliate Program with 30 days' written notice. Earned commissions for completed placements will be honored post-termination.\n\n**9. Modifications**\nHireTruckers365 may modify this Agreement at any time. Continued participation constitutes acceptance of updated terms.`,
};

function renderContent(text: string) {
  return text.split('\n').map((line, i) => {
    if (line.startsWith('**') && line.endsWith('**') && line.length > 4) {
      const inner = line.slice(2, -2);
      return <p key={i} className="font-bold text-zinc-100 mt-5 mb-1 first:mt-0">{inner}</p>;
    }
    if (line.startsWith('- ')) {
      return <li key={i} className="text-zinc-400 text-sm ml-4 list-disc">{line.slice(2)}</li>;
    }
    if (line.trim() === '') return <div key={i} className="h-2" />;
    return <p key={i} className="text-zinc-400 text-sm leading-relaxed">{line}</p>;
  });
}

function LegalContent() {
  const searchParams = useSearchParams();
  const sectionParam = searchParams.get('section');
  const validSection = SECTIONS.find((s) => s.id === sectionParam)?.id ?? 'terms';

  const [activeSection, setActiveSection] = useState(validSection);
  const [content, setContent] = useState<Record<string, string>>(DEFAULT_CONTENT);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (sectionParam && SECTIONS.find((s) => s.id === sectionParam)) {
      setActiveSection(sectionParam);
    }
  }, [sectionParam]);

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('legal_documents')
      .select('section_id, content')
      .then(({ data }) => {
        if (data && data.length > 0) {
          const map: Record<string, string> = { ...DEFAULT_CONTENT };
          data.forEach((row: { section_id: string; content: string }) => {
            if (row.content && row.content.trim().length > 0) {
              map[row.section_id] = row.content;
            }
          });
          setContent(map);
        }
        setLoading(false);
      });
  }, []);

  const active = SECTIONS.find((s) => s.id === activeSection)!;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Header */}
      <header className="bg-zinc-900 border-b border-zinc-800 sticky top-0 z-30">
        <div className="max-w-screen-xl mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link href="/landing-page" className="flex items-center gap-2 text-zinc-400 hover:text-zinc-100 transition-colors text-sm">
              <ArrowLeft size={15} />
              <span className="hidden sm:inline">Back</span>
            </Link>
            <span className="text-zinc-700">|</span>
            <div className="flex items-center gap-2">
              <AppLogo size={22} />
              <span className="font-bold text-sm text-zinc-100">
                HireTruckers<span className="text-amber-400">365</span>
              </span>
            </div>
          </div>
          <span className="text-xs text-zinc-500 font-medium tracking-wide uppercase">Legal Documents</span>
        </div>
      </header>

      <div className="max-w-screen-xl mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-zinc-100 mb-1">Legal &amp; Policies</h1>
          <p className="text-sm text-zinc-500">Review our platform agreements, policies, and fee structures.</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar nav */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
              {SECTIONS.map((section) => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-all duration-150 border-b border-zinc-800 last:border-b-0
                      ${isActive ? 'bg-zinc-800' : 'hover:bg-zinc-800/50'}`}
                  >
                    <div className={`w-8 h-8 rounded-lg border flex items-center justify-center flex-shrink-0 ${section.bg}`}>
                      <Icon size={15} className={section.color} />
                    </div>
                    <span className={`text-sm font-medium flex-1 ${isActive ? 'text-zinc-100' : 'text-zinc-400'}`}>
                      {section.label}
                    </span>
                    {isActive && <ChevronRight size={14} className="text-zinc-500" />}
                  </button>
                );
              })}
            </div>

            <div className="mt-4 bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <p className="text-xs text-zinc-500 leading-relaxed">
                Questions about our policies? Contact us at{' '}
                <a href="mailto:legal@hiretruckers365.com" className="text-amber-400 hover:underline">
                  legal@hiretruckers365.com
                </a>
              </p>
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0">
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl">
              {/* Doc header */}
              <div className="flex items-center gap-3 px-6 py-4 border-b border-zinc-800 rounded-t-xl">
                <div className={`w-9 h-9 rounded-lg border flex items-center justify-center ${active.bg}`}>
                  <active.icon size={17} className={active.color} />
                </div>
                <div>
                  <h2 className="font-bold text-zinc-100 text-base">{active.label}</h2>
                  <p className="text-xs text-zinc-500">HireTruckers365 — Last updated April 2026</p>
                </div>
              </div>

              {/* Doc body */}
              <div className="px-6 py-6">
                {loading ? (
                  <div className="space-y-3">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className={`h-3 bg-zinc-800 rounded animate-pulse ${i % 3 === 0 ? 'w-1/3' : 'w-full'}`} />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-0.5">
                    {renderContent(content[activeSection] || DEFAULT_CONTENT[activeSection])}
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default function LegalPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    }>
      <LegalContent />
    </Suspense>
  );
}
